import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { CommonModule } from '@angular/common';
import { ProductDetailsCart, calculateCartTotalPointsAtUserRate, hasSpecialRateProducts } from '@models';
import { SkeletonTemplateService } from '@skeleton-loader';
import { TaxService, UserService } from '@core';
import { DigitsGroupingPipe } from './../pipes/digits-grouping.pipe';
import { CartOldPriceComponent } from '../cart-old-price/cart-old-price.component';

@Component({
  selector: 'lib-cart-total-info',
  imports: [CommonModule, DigitsGroupingPipe, CartOldPriceComponent],
  templateUrl: './cart-total-info.component.html',
  styleUrl: './cart-total-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartTotalInfoComponent {
  data = input<ProductDetailsCart | null>();
  hideTax = input(false);
  totalLabel = input<string>('Total');
  role = input<string>();
  ariaLive = input<string>();

  private readonly taxService = inject(TaxService);
  private readonly userService = inject(UserService);

  skeletonService = inject(SkeletonTemplateService);
  readonly canRedeemPoints = inject(UserService).canRedeemPoints;

  readonly vm = computed(() => ({
    productTax: this.taxService.productTax(),
  }));

  readonly discountedTaxPoints = computed(() => this.vm().productTax?.discountedTaxAmountPoints ?? null);

  // Only show the strikethrough (non-accelerated) tax when it is actually higher
  // than the accelerated value. For a 0 tax the backend returns 0 for both, so
  // there is nothing to cross out (RID-1169).
  readonly showTaxStrikethrough = computed(() => {
    const discounted = this.discountedTaxPoints();
    const standard = this.vm().productTax?.taxAmountPoints ?? 0;
    return discounted != null && standard > discounted;
  });

  readonly hasSpecialRateProducts = computed(() =>
    hasSpecialRateProducts(this.data() ?? null, this.userService.conversionRate()),
  );

  readonly subtotalPointsStandardRate = computed(() => {
    return this.hasSpecialRateProducts()
      ? calculateCartTotalPointsAtUserRate(this.data() ?? null, this.userService.conversionRate(), false)
      : null;
  });

  readonly totalPoints = computed(() => {
    const value = this.data()?.subtotalPoints;
    const taxPoints = this.discountedTaxPoints() ?? this.vm().productTax?.taxAmountPoints ?? 0;
    const total = this.hideTax() ? value : value + taxPoints;
    return Math.floor(total);
  });

  readonly totalPointsStandardRate = computed(() => {
    if (!this.hasSpecialRateProducts()) return null;
    const value = calculateCartTotalPointsAtUserRate(this.data() ?? null, this.userService.conversionRate(), false);
    return this.hideTax() ? value : value + (this.vm().productTax?.taxAmountPoints || 0);
  });

  readonly totalPrice = computed(() => {
    return this.hideTax()
      ? this.data()!.subtotalPrice
      : this.data()!.subtotalPrice + (this.vm().productTax?.taxAmountUSD || 0);
  });

  readonly totalDiscountedPointsStandardRate = computed(() => {
    if (!this.hasSpecialRateProducts()) return null;
    const value = calculateCartTotalPointsAtUserRate(this.data() ?? null, this.userService.conversionRate(), true);

    return Math.floor(this.hideTax() ? value : value + (this.vm().productTax?.taxAmountPoints || 0));
  });

  readonly totalDiscountedPrice = computed(() => {
    return this.hideTax()
      ? this.data()!.subtotalDiscountedPrice
      : this.data()!.subtotalDiscountedPrice + (this.vm().productTax?.taxAmountUSD || 0);
  });
}
